import React, { createContext, useContext, useEffect, useReducer, useState } from 'react'
import eventsReducer from './eventsReducer'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../services/firebase'
import { SET_EVENTS, SET_EVENTS_LOADING } from '../types'
import { AuthContext } from '../auth/authState'
import EventModal from '../../components/EventModal'
import { NotificationContext } from '../notification/notificationState'
import { FirebaseError } from 'firebase/app'


const EventsContext = createContext()

const EventsState = ({ children }) => {
  const initialState = {
    events: [],
    eventsLoading: false,
    eventsLoaded: false
  }

  const [state, dispatch] = useReducer(eventsReducer, initialState)
  const { authStateLoaded, isUserAuthenticated } = useContext(AuthContext)
  const { toast } = useContext(NotificationContext)

  const [eventModal, setEventModal] = useState({
    isOpen: false,
    event: null,
  })

  useEffect(() => {
    if(!authStateLoaded) return
    if(authStateLoaded && !isUserAuthenticated) {
      // clear events
      clearEvents()
    } else if(authStateLoaded && isUserAuthenticated) {
      fetchEvents()
    }
  }, [authStateLoaded, isUserAuthenticated])

  const clearEvents = () => {
    dispatch({ type: SET_EVENTS, payload: [] })
  }

  const fetchEvents = async () => {
    dispatch({ type: SET_EVENTS_LOADING, payload: true })
    const fetchEventsFunction = httpsCallable(functions, "fetchMyEvents")
    const response = await fetchEventsFunction()
    dispatch({ type: SET_EVENTS, payload: response.data })    
  }

  const createEvent = async (eventData) => {
    
    const createEventFunction = httpsCallable(functions, "createEvent")
    const response = await createEventFunction(eventData)

    const events = [...state.events, response.data]
    dispatch({ type: SET_EVENTS, payload: events })
    return response.data
  }

  const fetchEvent = async (id) => {
    try {
      dispatch({ type: SET_EVENTS_LOADING, payload: true })
      const fetchEventFunction = httpsCallable(functions, "fetchEvent")
      const response = await fetchEventFunction({ id })
      const event = response.data
      const events = [...state.events]
      if(!events.find(e => e.id === event.id)) {
        events.push(event)
      }
      dispatch({ type: SET_EVENTS, payload: events })   
      return event 
    } catch (error) {
      console.error(error)
      dispatch({ type: SET_EVENTS, payload: [...(state.events || [])] })
      return {
        error: error
      }
    }
  }

  const updateEvent = async (id, eventData) => {
    const updateEventFunction = httpsCallable(functions, "updateEvent")
    const response = await updateEventFunction({ id, ...eventData })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === id)
    events[eventIndex] = response.data
    dispatch({ type: SET_EVENTS, payload: events })
  }

  const addVehicleToEvent = async (eventId, vehicleData) => {
    const addVehicleFunction = httpsCallable(functions, "addVehicleToEvent")
    const response = await addVehicleFunction({ eventId, vehicle: vehicleData })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === eventId)
    events[eventIndex] = response.data
    // const event = events.find(e => e.id === eventId)
    // event.vehicles.push(response.data)
    dispatch({ type: SET_EVENTS, payload: events })    
  }

  const updateEventVehicle = async (eventId, vehicleIndex, vehicleData) => {
    const updateVehicleFunction = httpsCallable(functions, "updateVehicle")
    const response = await updateVehicleFunction({ eventId, vehicleIndex, vehicle: vehicleData })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === eventId)
    events[eventIndex] = response.data
    dispatch({ type: SET_EVENTS, payload: events })
  }

  const joinVehicle = async (eventId, vehicleIndex, data) => {
    try {
      const joinVehicleFunction = httpsCallable(functions, "joinVehicle")
      const response = await joinVehicleFunction({ eventId, vehicleIndex, ...data })

      const events = [...state.events]
      const eventIndex = events.findIndex(e => e.id === eventId)
      events[eventIndex] = response.data
      dispatch({ type: SET_EVENTS, payload: events })

      return response.data
    } catch (error) {
      toast({ message: error.message, variant: 'error' })
    }
  }

  const joinWaitlist = async (eventId, data) => {
    const joinWaitlistFunction = httpsCallable(functions, "joinWaitlist")
    const response = await joinWaitlistFunction({ eventId, ...data })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === eventId)
    events[eventIndex] = response.data
    dispatch({ type: SET_EVENTS, payload: events })

    return response.data
  }

  const deleteEvent = async (eventId) => {
    const deleteEventFunction = httpsCallable(functions, "deleteEvent")
    await deleteEventFunction({ eventId })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === eventId)
    events.splice(eventIndex, 1)
    dispatch({ type: SET_EVENTS, payload: events })
  }

  const leaveVehicle = async (eventId, vehicleIndex) => {
    const leaveVehicleFunction = httpsCallable(functions, "leaveEvent")
    const response = await leaveVehicleFunction({ eventId, vehicleIndex })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === eventId)
    events[eventIndex] = response.data
    dispatch({ type: SET_EVENTS, payload: events })

    return response.data
  }

  const leaveWaitlist = async (eventId) => {
    const leaveWaitlistFunction = httpsCallable(functions, "leaveEvent")
    const response = await leaveWaitlistFunction({ eventId, waitlist: true })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === eventId)
    events[eventIndex] = response.data
    dispatch({ type: SET_EVENTS, payload: events })

    return response.data
  }

  const removeVehicleFromEvent = async (eventId, vehicleIndex) => {
    const removeVehicleFunction = httpsCallable(functions, "removeVehicleFromEvent")
    const response = await removeVehicleFunction({ eventId, vehicleIndex })

    const events = [...state.events]
    const eventIndex = events.findIndex(e => e.id === eventId)
    events[eventIndex] = response.data
    dispatch({ type: SET_EVENTS, payload: events })

    return response.data
  }

  const promptCreateEvent = () => {
    // open modal
    setEventModal({ isOpen: true, event: null })
  }

  const promptEditEvent = (event) => {
    // open modal
    setEventModal({ isOpen: true, event })
  }

  const closeEventModal = () => {
    // close modal
    setEventModal({ isOpen: false, event: null })
  }

  return (
    <EventsContext.Provider value={{
      events: state.events,
      eventsLoading: state.eventsLoading,
      eventsLoaded: state.eventsLoaded,
      createEvent,
      updateEvent,
      fetchEvent,
      addVehicleToEvent,
      updateEventVehicle,
      joinVehicle,
      joinWaitlist,
      deleteEvent,
      removeVehicleFromEvent,
      leaveVehicle,
      leaveWaitlist,
      promptCreateEvent,
      promptEditEvent,
    }}>
      {eventModal.isOpen &&
        <EventModal showModal={eventModal.isOpen} onClose={closeEventModal} event={eventModal.event} />
      }
      {children}
    </EventsContext.Provider>
  )
}

export default EventsState
export { EventsContext }
